import React, { useState, useEffect, useRef } from 'react';

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

interface GameTimerDisplayProps {
  seconds: number;
  isActive: boolean;
  playerColor: 'white' | 'black';
  timerEnabled: boolean;
  gameMode: string;
  isGameStarted: boolean;
  gameStatus: string;
  onTimeout?: () => void;
  onTimerWarning?: (player: 'white' | 'black') => void;
  onTimerStopped?: (player: 'white' | 'black', remainingSeconds: number) => void;
  timerWarningShown?: boolean;
}

/**
 * Isolated timer display for local/AI games.
 * Owns its own RAF loop and display state so only this component re-renders on tick,
 * not the entire App. For online games, just displays the prop value.
 */
export const GameTimerDisplay: React.FC<GameTimerDisplayProps> = ({
  seconds,
  isActive,
  playerColor,
  timerEnabled,
  gameMode,
  isGameStarted,
  gameStatus,
  onTimeout,
  onTimerWarning,
  onTimerStopped,
  timerWarningShown = false,
}) => {
  const [displaySeconds, setDisplaySeconds] = useState(seconds);
  const timerStartTimeRef = useRef<number | null>(null);
  const timerInitialRef = useRef(seconds);
  const animationFrameRef = useRef<number | null>(null);
  const lastDisplayRef = useRef(seconds);
  const warnedRef = useRef(false);

  const isLocalTimer = gameMode !== 'online';
  const shouldRun = isLocalTimer && timerEnabled && isGameStarted && gameStatus === 'active' && isActive;

  // Sync from props when switching players or when parent updates (e.g. increment)
  useEffect(() => {
    if (!shouldRun) {
      if (isActive) {
        setDisplaySeconds(seconds);
        lastDisplayRef.current = seconds;
      } else {
        if (timerStartTimeRef.current !== null && onTimerStopped) {
          onTimerStopped(playerColor, lastDisplayRef.current);
        }
        setDisplaySeconds(seconds);
        lastDisplayRef.current = seconds;
        timerStartTimeRef.current = null;
        warnedRef.current = false;
      }
      return;
    }

    // Starting or resuming this player's timer
    if (timerStartTimeRef.current === null) {
      timerStartTimeRef.current = Date.now();
      timerInitialRef.current = seconds;
      warnedRef.current = false;
    }
  }, [shouldRun, isActive, seconds, playerColor, onTimerStopped]);

  // RAF loop for local countdown - only this component re-renders
  useEffect(() => {
    if (!shouldRun) {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (!isActive) {
        timerStartTimeRef.current = null;
      }
      return;
    }

    const updateDisplay = () => {
      const startTime = timerStartTimeRef.current;
      if (startTime === null) return;

      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      const remaining = Math.max(0, timerInitialRef.current - elapsed);

      // 20-second warning (once per player per game)
      if (remaining <= 20 && remaining > 0 && !warnedRef.current && !timerWarningShown) {
        warnedRef.current = true;
        onTimerWarning?.(playerColor);
      }

      if (remaining <= 0) {
        if (animationFrameRef.current !== null) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        timerStartTimeRef.current = null;
        onTimeout?.();
        return;
      }

      if (remaining !== lastDisplayRef.current) {
        lastDisplayRef.current = remaining;
        setDisplaySeconds(remaining);
      }

      animationFrameRef.current = requestAnimationFrame(updateDisplay);
    };

    animationFrameRef.current = requestAnimationFrame(updateDisplay);

    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [shouldRun, isActive, playerColor, onTimeout, onTimerWarning, timerWarningShown]);

  // Recalculate when tab becomes visible (user may have been away)
  useEffect(() => {
    if (!isLocalTimer) return;

    const handleVisibility = () => {
      if (document.hidden || !shouldRun) return;
      const startTime = timerStartTimeRef.current;
      if (startTime === null) return;
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      const remaining = Math.max(0, timerInitialRef.current - elapsed);
      if (remaining !== lastDisplayRef.current) {
        lastDisplayRef.current = remaining;
        setDisplaySeconds(remaining);
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [isLocalTimer, shouldRun]);

  if (!timerEnabled) {
    return null;
  }

  const valueToShow = isLocalTimer && isActive ? displaySeconds : seconds;

  return (
    <div
      className={`player-timer ${isActive ? 'active' : ''}`}
      id={`${playerColor}-timer`}
    >
      {formatTime(valueToShow)}
    </div>
  );
};
